import React, { Component, type ReactNode } from 'react';
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';
import { Button } from '../ui/Button/Button';
import { Card } from '../ui/Card/Card';
import styles from './ErrorBoundary.module.css';

import { logger } from '../../utils/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Use `logger.debug` for the local trace: `logger.error` would *also*
    // forward to `window.reportError`, which we then call below with richer
    // metadata. Dev gets the console output, prod stays silent here.
    logger.debug('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Single authoritative report to the monitoring seam (local dev-console
    // stub only — no third-party processor; see src/utils/monitoring.ts).
    if (typeof window !== 'undefined' && (window as any).reportError) {
      (window as any).reportError(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.errorBoundary}>
          <div className={styles.container}>
            <Card className={styles.errorCard}>
              <div className={styles.errorIcon}>
                <FiAlertTriangle />
              </div>
              
              <div className={styles.errorContent}>
                <h2>Something went wrong</h2>
                <p>
                  An unexpected error occurred. This might be due to a temporary issue 
                  or a problem with the component you were trying to use.
                </p>
                
                
                <div className={styles.errorActions}>
                  <Button
                    onClick={this.handleReset}
                    variant="primary"
                    icon={<FiRefreshCw />}
                  >
                    Try Again
                  </Button>
                  
                  <Button
                    onClick={this.handleReload}
                    variant="secondary"
                    icon={<FiRefreshCw />}
                  >
                    Reload Page
                  </Button>
                  
                  <Button
                    onClick={this.handleGoHome}
                    variant="ghost"
                    icon={<FiHome />}
                  >
                    Go Home
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// Hook-based version for functional components
export function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
}

// Specialized ErrorBoundary for Modal components
export function ModalErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={
        <div className={styles.modalErrorFallback}>
          <div className={styles.modalErrorContent}>
            <FiAlertTriangle className={styles.modalErrorIcon} />
            <h3>Modal Error</h3>
            <p>The modal component encountered an error. Please try again.</p>
            <Button
              onClick={() => window.location.reload()}
              variant="primary"
              size="sm"
            >
              Reload Page
            </Button>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
